import { addDays, format, parseISO } from 'date-fns';
import { el } from 'date-fns/locale';
import { TNaerasAdjustPayload, TNaerasBid, TNaerasBidProgram, TNaerasSettings } from '@/data/types';
import { DayOffer } from '../types';

/** Tolerance the server uses when checking a value against the step grid. */
const STEP_EPSILON = 1e-6;

/**
 * Ceiling of "ΜΥΗΣ Κάτω Προεσπέρας + Υβριδική" per 12-hour block, in MWh: the
 * unit produces at most 3.1 MW, for 12 hours. It holds in both seasons — in the
 * dry one Kato is 0, so it caps the hybrid block on its own.
 */
export const BLOCK_PRODUCTION_CEILING = 37.2;

/** The hourly ΜΥΗΣ ranges, in MW, as the client fixed them for every season. */
export const HOURLY_LIMITS = {
  upper: { min: 0, max: 1, step: 0.1 },
  lower: { min: 0, max: 3, step: 0.1 },
};

const BLOCK_HOURS: [number[], number[]] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
];

/** "Σάββατο 05/09", capitalised the way the Greek locale doesn't. */
export function formatDayLabel(dateStr: string): string {
  const label = format(parseISO(dateStr), 'EEEE dd/MM', { locale: el });

  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * A final all-zero bid the backend wrote for a day it could not compute.
 *
 * There is no flag on the row, so it is recognised by its shape: written by a
 * job, locked automatically, and every computed value 0. A user's own draft is
 * never replaced by one, so this can't match a bid someone worked on.
 */
export function isZeroBid(bid: TNaerasBid): boolean {
  const { Ano_Proespera = [], Kato_Proespera = [], Hybrid_12h = [] } = bid.computed ?? {};

  return (
    bid.source === 'job' &&
    bid.auto_finalized &&
    [...Ano_Proespera, ...Kato_Proespera, ...Hybrid_12h].every((value) => Number(value) === 0)
  );
}

/**
 * Projects a server bid onto the view model the screens render.
 *
 * `program` is the version in effect (`final ?? adjusted ?? computed`), so the
 * editor always shows what would actually be submitted.
 */
export function bidToDayOffer(bid: TNaerasBid): DayOffer {
  const program = bid.program;

  return {
    date: bid.target_date,
    dayLabel: formatDayLabel(bid.target_date),
    mode: bid.mode,
    hourly: Array.from({ length: 24 }, (_, hour) => ({
      hour,
      mysAno: Number(program.Ano_Proespera?.[hour] ?? 0),
      mysKato: Number(program.Kato_Proespera?.[hour] ?? 0),
    })),
    hybrid1st12h: Number(program.Hybrid_12h?.[0] ?? 0),
    hybrid2nd12h: Number(program.Hybrid_12h?.[1] ?? 0),
    hybridPoolCapacity: bid.hybrid_pool_mwh,
    isFinalized: bid.status === 'final',
    // Three ways a day gets locked, and the operator has to tell them apart:
    // the 23:30 job did it unattended, the user confirmed the exported values,
    // or the user uploaded the file that was actually sent.
    finalSource:
      bid.status !== 'final'
        ? undefined
        : bid.auto_finalized
        ? 'auto'
        : bid.has_final_file
        ? 'excel'
        : 'ui',
    finalizedAt: bid.finalized_at ?? undefined,
    originalExcelName: bid.final_file_name ?? undefined,
    autoFinalized: Boolean(bid.auto_finalized),
    isZeroBid: isZeroBid(bid),
    hasPendingRevision: Boolean(bid.has_pending_revision),
    revisedAt: bid.revised_at ?? undefined,
    hybridCorrectionMwh: Number(bid.hybrid_correction_mwh ?? 0),
    status: bid.status,
    limits: bid.limits,
    hybridWarnings: bid.warnings ?? [],
    computed: {
      mysAno: bid.computed.Ano_Proespera,
      mysKato: bid.computed.Kato_Proespera,
      hybrid: bid.computed.Hybrid_12h,
    },
  };
}

/** The program as the PATCH body wants it. */
export function dayOfferToProgram(offer: DayOffer): TNaerasBidProgram {
  return {
    Ano_Proespera: offer.hourly.map((h) => h.mysAno),
    Kato_Proespera: offer.hourly.map((h) => h.mysKato),
    Hybrid_12h: [offer.hybrid1st12h, offer.hybrid2nd12h],
  };
}

/**
 * The PATCH body for an edited offer: only the parts that differ from the
 * server's `draft` (what the editor works on), each array taken whole. Omitted
 * parts keep their draft value, and the server only checks the hours a request
 * changes. In the dry season the hourly series are not adjustable at all.
 */
export function adjustPayload(offer: DayOffer, saved: TNaerasBid | undefined): TNaerasAdjustPayload {
  const program = dayOfferToProgram(offer);
  const base = saved?.draft ?? saved?.program;
  const differs = (a: number[], b: number[] | undefined) =>
    !b || a.length !== b.length || a.some((value, i) => Math.abs(value - Number(b[i])) > STEP_EPSILON);

  const payload: TNaerasAdjustPayload = {};

  if (differs(program.Hybrid_12h, base?.Hybrid_12h)) payload.Hybrid_12h = program.Hybrid_12h;

  if (offer.mode !== 'summer_mode') {
    if (differs(program.Ano_Proespera, base?.Ano_Proespera)) payload.Ano_Proespera = program.Ano_Proespera;
    if (differs(program.Kato_Proespera, base?.Kato_Proespera)) payload.Kato_Proespera = program.Kato_Proespera;
  }

  return payload;
}

/**
 * The value on the grid nearest the allowed range, on the given side.
 *
 * The grid is `computed ± n × step`, so re-entering the range is a matter of
 * finding the right `n` rather than clamping to the bound itself — `min` and
 * `max` are almost never on the grid.
 */
function gridValueInsideRange(
  computed: number,
  limit: { step: number; min: number; max: number },
  side: 'below-max' | 'above-min'
): number | null {
  const steps =
    side === 'below-max'
      ? Math.floor((limit.max - computed) / limit.step + STEP_EPSILON)
      : Math.ceil((limit.min - computed) / limit.step - STEP_EPSILON);
  const value = computed + steps * limit.step;

  if (value < limit.min - STEP_EPSILON || value > limit.max + STEP_EPSILON) {
    return null;
  }

  return Number(value.toFixed(6));
}

/**
 * The next allowed value in the step grid.
 *
 * NAERAS returns continuous values such as 0.5273, and the allowed values are
 * `computed ± n × step` — which is why the UI offers +/− buttons rather than a
 * free text field.
 *
 * A single step is not always enough to reach the range. The grid is anchored on
 * the model's own output, and that output can sit outside the limits the server
 * enforces — a winter `Kato_Proespera` is the lower tank's latest energy reading
 * repeated across all 24 hours, which can exceed its 2.6 MW ceiling. Stepping
 * one increment from there lands out of range again, so refusing the press left
 * the control dead in both directions with no way back in. A press aimed at the
 * range therefore jumps to the nearest grid value inside it.
 */
export function stepValue(
  current: number,
  computed: number,
  delta: number,
  limit: { step: number; min: number; max: number } | null
): number {
  if (!limit) return current;

  const direction = Math.sign(delta);
  const steps = Math.round((current - computed) / limit.step) + direction;
  const next = computed + steps * limit.step;

  if (next >= limit.min - STEP_EPSILON && next <= limit.max + STEP_EPSILON) {
    return Number(next.toFixed(6));
  }

  // Out of range. Only a press pointing at the range gets to skip ahead: from
  // above the ceiling that is a decrease, from below the floor an increase.
  const entry =
    current > limit.max + STEP_EPSILON && direction < 0
      ? gridValueInsideRange(computed, limit, 'below-max')
      : current < limit.min - STEP_EPSILON && direction > 0
      ? gridValueInsideRange(computed, limit, 'above-min')
      : null;

  if (entry === null || Math.sign(entry - current) !== direction) {
    return current;
  }

  return entry;
}

/** Whether a further step in this direction would leave the allowed range. */
export function canStep(
  current: number,
  computed: number,
  delta: number,
  limit: { step: number; min: number; max: number } | null
): boolean {
  if (!limit) return false;

  return stepValue(current, computed, delta, limit) !== current;
}

/** The day after `date`, as YYYY-MM-DD. */
export function nextDate(date: string): string {
  return format(addDays(parseISO(date), 1), 'yyyy-MM-dd');
}

/** The hybrid stock one day's two blocks are checked against. */
export interface DayPoolBudget {
  /** Estimated stock at 00:00 of tomorrow — the same figure for every day. */
  base: number;
  /** What is left of `base` once the days before this one have drawn on it. */
  remaining: number;
  /**
   * Past tomorrow, pumping in the meantime can refill the pool, so `remaining`
   * is a guide rather than a ceiling — the 12h production check is the limit.
   */
  pumpable: boolean;
}

/**
 * How much of the pool a day can draw on.
 *
 * Tomorrow, and any day already behind it, gets its own pool as a hard ceiling.
 * A later day gets tomorrow's pool minus everything bid from tomorrow up to the
 * day before it — every day drawing on the same stock, not on a figure each
 * calculation run re-estimated on its own.
 */
export function dayPoolBudget(
  offer: DayOffer,
  tomorrow: string | undefined,
  offerFor: (date: string) => DayOffer | undefined
): DayPoolBudget {
  if (!tomorrow || offer.date <= tomorrow) {
    return {
      base: offer.hybridPoolCapacity,
      remaining: offer.hybridPoolCapacity,
      pumpable: false,
    };
  }

  const base = offerFor(tomorrow)?.hybridPoolCapacity ?? offer.hybridPoolCapacity;
  let used = 0;

  for (let date = tomorrow; date < offer.date; date = nextDate(date)) {
    const day = offerFor(date);
    if (day) used += day.hybrid1st12h + day.hybrid2nd12h;
  }

  return { base, remaining: base - used, pumpable: true };
}

/**
 * The ceiling of one hybrid block, given the other block and the Kato program.
 *
 * Mirrors `hybridBlockMax` on the server so the "Μέγιστο Έγχυσης" shown stays
 * right while the user edits; the authoritative value comes back with every
 * PATCH response. It is informative only — the +/− buttons may go past it.
 * `poolMwh` is null past tomorrow, where future pumping lifts the pool limit
 * and only the production ceiling is left.
 *
 * `limits.hybrid.max` is deliberately not used as the ceiling here. The server
 * computes it from the values as they were *saved*, and the whole job of this
 * function is to track the ceiling as the user moves the other block and the
 * Kato hours — the moment they lower the other block the real ceiling rises
 * above the number the server sent, and pinning to it would refuse a press the
 * server would have accepted. The rule itself is fixed and documented (both
 * blocks within the pool, and Kato + hybrid within 37.2 MWh per block), so
 * mirroring it is safe; the server's own answer arrives with the PATCH
 * response and replaces this estimate.
 */
export function hybridBlockMax(offer: DayOffer, block: 0 | 1, poolMwh: number | null): number {
  const other = block === 0 ? offer.hybrid2nd12h : offer.hybrid1st12h;
  const max = BLOCK_PRODUCTION_CEILING - katoBlockSum(offer, block);

  return poolMwh === null ? max : Math.min(max, poolMwh - other);
}

/** Sum of ΜΥΗΣ Κάτω Προεσπέρας over one 12-hour block, in MWh. */
export function katoBlockSum(offer: DayOffer, block: 0 | 1): number {
  return BLOCK_HOURS[block].reduce(
    (total, hour) => total + (Number(offer.hourly[hour]?.mysKato) || 0),
    0
  );
}

/** The filename the export endpoint sets, rebuilt from the requested dates. */
export function exportFileName(dates: string[]): string {
  const sorted = [...dates].sort();
  const suffix = sorted.length > 1 ? `_${sorted[sorted.length - 1]}` : '';

  return `naeras-bid-${sorted[0]}${suffix}.xlsx`;
}

/**
 * The `message_code` values the screens branch on. Everything else is left to
 * the global handler, which shows the server's Greek `message` as it is.
 */
export const NaerasErrorCodes = {
  OUT_OF_RANGE: 'NAERAS_BID_OUT_OF_RANGE',
  FILE_INVALID: 'NAERAS_BID_FILE_INVALID',
  NOT_FOUND: 'NAERAS_BID_NOT_FOUND',
  LOCKED: 'NAERAS_BID_LOCKED',
  NOT_ADJUSTABLE: 'NAERAS_BID_NOT_ADJUSTABLE',
  LIMIT_VIOLATION: 'NAERAS_BID_LIMIT_VIOLATION',
  REVISION_FAILED: 'NAERAS_BID_REVISION_FAILED',
  REQUEST_FAILED: 'NAERAS_REQUEST_FAILED',
  STATE_MISSING: 'NAERAS_STATE_MISSING',
  ACROSS_UNAVAILABLE: 'ACROSS_UNAVAILABLE',
  WET_SEASON_LOCKED: 'NAERAS_WET_SEASON_LOCKED',
  INVALID_RANGE: 'NAERAS_INVALID_RANGE',
} as const;

export interface NaerasApiError {
  code: string | null;
  message: string | null;
  /** `status_code` from the body — one code can arrive under several. */
  status: number | null;
  /** Every limit breach, when the server answered NAERAS_BID_LIMIT_VIOLATION. */
  errors: string[];
}

/**
 * Unpacks the error a rejected thunk carries.
 *
 * `Request` rejects with the stringified response body, so the Greek `message`
 * — which is written for the end user — survives all the way to the toast.
 */
export function parseNaerasError(error: unknown): NaerasApiError {
  const raw =
    typeof error === 'string'
      ? error
      : (error as { message?: string })?.message ?? '';

  try {
    const body = JSON.parse(raw);

    return {
      code: body?.message_code ?? body?.messageCode ?? body?.code ?? null,
      message: body?.message ?? null,
      status: body?.status_code ?? body?.statusCode ?? null,
      errors: Array.isArray(body?.extra?.errors) ? body.extra.errors : [],
    };
  } catch {
    return { code: null, message: null, status: null, errors: [] };
  }
}

/**
 * What to tell the user about a rejected write, when the code carries more than
 * the server's own sentence does.
 *
 * `NAERAS_BID_REVISION_FAILED` is the one worth expanding: the same code arrives
 * as 502 (the model or SCADA had no data for that day), 409 (no other final bid
 * could take the correction) and 422 (the correction would push a 12h block
 * below zero), and the three need different actions from the operator.
 */
export function naerasWriteErrorMessage(error: unknown, fallback: string): string {
  const { code, message, status, errors } = parseNaerasError(error);

  if (code === NaerasErrorCodes.LIMIT_VIOLATION && errors.length) {
    return errors.join(' ');
  }

  // Finalizing a future day re-runs Job B, which needs ACROSS; a zero bid
  // stays final in the meantime, so nothing was lost
  if (code === NaerasErrorCodes.ACROSS_UNAVAILABLE) {
    return 'Το ACROSS δεν είναι διαθέσιμο — δοκιμάστε αργότερα. Η τρέχουσα οριστική προσφορά παραμένει σε ισχύ.';
  }

  if (code === NaerasErrorCodes.REVISION_FAILED) {
    const reason =
      status === 502
        ? 'Δεν υπάρχουν διαθέσιμα δεδομένα NAERAS/SCADA για την ημέρα που αναθεωρείται.'
        : status === 409
        ? 'Δεν υπάρχει άλλη οριστική προσφορά για να απορροφήσει τη διόρθωση.'
        : status === 422
        ? 'Η διόρθωση θα έκανε αρνητικό ένα 12ωρο της τελευταίας οριστικής προσφοράς.'
        : '';

    return [message, reason].filter(Boolean).join(' ') || fallback;
  }

  return message || fallback;
}

/** The wet season as the backend fixes it, used until `/naeras/settings` answers. */
const DEFAULT_WET_SEASON_WINDOW = ['10-01', '04-15'] as const;

/**
 * "1/10–15/4", from the settings' MM-DD window. The shape of `window` is read
 * loosely so a renamed pair of keys still labels the season.
 */
export function wetSeasonWindowLabel(window?: TNaerasSettings['window'] | null): string {
  const [from, to] = Array.isArray(window)
    ? window
    : window && 'from' in window
    ? [window.from, window.to]
    : window && 'start' in window
    ? [window.start, window.end]
    : DEFAULT_WET_SEASON_WINDOW;

  const dayMonth = (mmdd: string) => {
    const [month, day] = mmdd.split('-').map(Number);

    return `${day}/${month}`;
  };

  return `${dayMonth(from)}–${dayMonth(to)}`;
}
